import React, { useMemo, useState } from 'react';
import clientConfig from '../config/clientConfig';

const Reservation = ({ theme }) => {
  const reservationTexts = clientConfig.texts?.reservation || {};
  const ui = reservationTexts.ui || {};

  /* =============================
     STATE
  ============================= */
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    people: '',
    service: '',
    email: ''
  });

  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState(''); // 'success' | 'error'
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* =============================
     DATE DU JOUR
  ============================= */
  const todayISO = useMemo(() => {
    const d = new Date();
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0')
    ].join('-');
  }, []);

  /* =============================
     HANDLERS
  ============================= */
  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  /* =============================
     CONFIG (clients.js)
  ============================= */
  const allowedTimes = useMemo(
    () => (Array.isArray(ui.times) ? ui.times.filter(Boolean) : []),
    [ui.times]
  );

  const services = useMemo(
    () =>
      Array.isArray(ui.services)
        ? ui.services.filter((s) => s && (s.value || s.label))
        : [],
    [ui.services]
  );

  const labels = ui.labels || {};
  const messages = ui.messages || {};
  const aria = ui.aria || {};

  /* =============================
     VALIDATION
  ============================= */
  const validate = () => {
    const required = ui.requiredFields || [
      'name',
      'phone',
      'date',
      'time',
      'people',
      'service'
    ];

    const missing = required.some(
      (k) => !String(formData[k] || '').trim()
    );
    if (missing) return messages.fillAll || '';

    const p = Number(formData.people);
    if (!Number.isFinite(p) || p <= 0)
      return messages.peopleInvalid || '';

    if (formData.date < todayISO)
      return messages.datePast || '';

    if (
      allowedTimes.length &&
      formData.time &&
      !allowedTimes.includes(formData.time)
    )
      return messages.timeInvalid || '';

    if (
      services.length &&
      formData.service &&
      !services.some((s) => String(s.value) === String(formData.service))
    )
      return messages.serviceInvalid || '';

    return '';
  };

  /* =============================
     SUBMIT
  ============================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setStatus('');
    setStatusType('');

    const error = validate();
    if (error) {
      setStatus(error);
      setStatusType('error');
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;

      const tokenRes = await fetch(`${API_URL}/api/csrf-token`, {
        credentials: 'include'
      });

      if (!tokenRes.ok) {
        setStatus(messages.connection || '');
        setStatusType('error');
        setIsSubmitting(false);
        return;
      }

      const { csrfToken } = await tokenRes.json();

      const res = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          people: String(formData.people).trim()
        })
      });

      if (res.ok) {
        setStatus(messages.success || '');
        setStatusType('success');
        setFormData({
          name: '',
          phone: '',
          date: '',
          time: '',
          people: '',
          service: '',
          email: ''
        });
      } else {
        setStatus(messages.generic || '');
        setStatusType('error');
      }
    } catch {
      setStatus(messages.connection || '');
      setStatusType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* =============================
     RENDER INPUTS
  ============================= */
  const renderInputs = (prefix) => {
    const statusId = `${prefix}-reservation-status`;

    return (
      <>
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={labels.name || ''}
          required
          autoComplete="name"
          className={`${prefix}-input`}
          disabled={isSubmitting}
          aria-describedby={status ? statusId : undefined}
        />

        <input
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder={labels.phone || ''}
          required
          inputMode="tel"
          autoComplete="tel"
          className={`${prefix}-input`}
          disabled={isSubmitting}
        />

        <input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          min={todayISO}
          required
          className={`${prefix}-input`}
          disabled={isSubmitting}
        />

        <select
          name="time"
          value={formData.time}
          onChange={handleChange}
          required
          className={`${prefix}-input`}
          disabled={isSubmitting}
        >
          <option value="">{labels.timePlaceholder || ''}</option>
          {allowedTimes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <input
          type="number"
          name="people"
          value={formData.people}
          onChange={handleChange}
          min="1"
          placeholder={labels.people || ''}
          required
          className={`${prefix}-input`}
          disabled={isSubmitting}
        />

        {services.length ? (
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            required
            className={`${prefix}-input`}
            disabled={isSubmitting}
          >
            <option value="">{labels.servicePlaceholder || ''}</option>
            {services.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            name="service"
            value={formData.service}
            onChange={handleChange}
            placeholder={labels.service || ''}
            required
            className={`${prefix}-input`}
            disabled={isSubmitting}
          />
        )}

        <input
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={labels.emailOptional || ''}
          autoComplete="email"
          className={`${prefix}-input`}
          disabled={isSubmitting}
        />
      </>
    );
  };

  /* =============================
     THEME RESOLUTION (SAFE)
  ============================= */
  const resolvedTheme = useMemo(() => {
    const t = String(theme || '').trim().toLowerCase();
    return ['theme1', 'theme2', 'theme3', 'theme4', 'theme5'].includes(t)
      ? t
      : 'theme2';
  }, [theme]);

  /* =============================
     RENDER
  ============================= */
  const renderForm = (sectionClass, titleClass, formClass, msgClass) => (
    <section
      id="reservation"
      className={sectionClass}
      aria-label={aria.sectionLabel || ''}
    >
      <div className={`${sectionClass}-container`}>
        <h2 className={titleClass}>{reservationTexts.title || ''}</h2>

        {ui.subtitle ? (
          <p className={`${sectionClass}-subtitle`}>{ui.subtitle}</p>
        ) : null}

        <form onSubmit={handleSubmit} className={formClass} noValidate>
          {renderInputs(sectionClass)}

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? labels.sending || ''
              : reservationTexts.button || ''}
          </button>

          {status ? (
            <p
              id={`${sectionClass}-reservation-status`}
              className={`${msgClass} ${msgClass}--${statusType}`}
              role="status"
            >
              {status}
            </p>
          ) : null}
        </form>
      </div>
    </section>
  );

  switch (resolvedTheme) {
    case 'theme1':
      return renderForm(
        'reservation-section',
        '',
        '',
        'confirmation-message'
      );

    case 'theme3':
      return renderForm(
        'theme3-reservation',
        'theme3-section-title',
        'theme3-reservation-form',
        'theme3-success'
      );

    case 'theme4':
      return renderForm(
        'theme4-reservation',
        'theme3-section-title',
        'theme4-reservation-form',
        'theme4-success'
      );

    case 'theme5':
      return renderForm(
        'theme5-reservation',
        'theme5-reservation-title',
        'theme5-reservation-form',
        'theme5-reservation-status'
      );

    case 'theme2':
    default:
      return renderForm(
        'theme2-reservation-section',
        'theme2-section-title',
        'theme2-reservation-form',
        'theme2-confirmation'
      );
  }
};

export default Reservation;
